import React from 'react';
import './RoomVoicePanel.css';

function initials(name) {
  return (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

const STATUS_LABELS = {
  idle: 'Idle',
  connecting: 'Connecting',
  connected: 'Live',
  reconnecting: 'Reconnecting',
  failed: 'Issue',
};

function getParticipantStateLabel(participant) {
  if (!participant.hasMicrophone) return 'Listening only';
  if (participant.isMuted) return 'Muted';
  if (participant.isSpeaking) return 'Speaking';
  if (participant.recentlySpoke) return 'Recently spoke';
  return 'Mic on';
}

export default function RoomVoicePanel({
  roomName,
  joined,
  participants,
  selfMuted,
  isMicInitializing,
  isSwitchingDevice,
  connectionState,
  statusDetail,
  error,
  needsAudioResume,
  speakingCount,
  noiseSuppressionEnabled,
  canPublishAudio,
  publishRestrictionReason,
  isRoomOwner,
  isVoiceLocked,
  audioInputDevices,
  audioOutputDevices,
  selectedAudioInputId,
  selectedAudioOutputId,
  canSelectAudioOutput,
  onJoin,
  onLeave,
  onRefreshDevices,
  onStartAudio,
  onToggleMute,
  onSelectAudioInput,
  onSelectAudioOutput,
  onToggleNoiseSuppression,
}) {
  return (
    <div className="room-voice-panel">
      <div className="room-voice-summary">
        <div>
          <div className="room-voice-title">Voice</div>
          <div className="room-voice-subtitle">
            {participants.length > 0 ? `${participants.length} live in ${roomName}` : 'Join room voice'}
          </div>
        </div>
        <span className={`room-voice-status is-${connectionState}`}>
          {STATUS_LABELS[connectionState] || connectionState}
        </span>
      </div>

      {(statusDetail || speakingCount > 0) && (
        <div className="room-voice-banner">
          <div>{statusDetail || `${speakingCount} speaking now`}</div>
          {statusDetail && speakingCount > 0 && (
            <div className="room-voice-banner-meta">{speakingCount} speaking now</div>
          )}
        </div>
      )}

      <div className="room-voice-actions">
        {!joined ? (
          <button
            className="room-voice-btn room-voice-btn-primary"
            onClick={onJoin}
            disabled={isMicInitializing}
          >
            {isMicInitializing ? 'Joining…' : 'Join Voice'}
          </button>
        ) : (
          <>
            <button
              className={`room-voice-btn ${selfMuted ? 'room-voice-btn-warning' : 'room-voice-btn-secondary'}`}
              onClick={onToggleMute}
              disabled={!canPublishAudio}
            >
              {selfMuted ? 'Unmute' : 'Mute'}
            </button>
            <button
              className="room-voice-btn room-voice-btn-secondary"
              onClick={onLeave}
            >
              Leave
            </button>
          </>
        )}

        <button
          className="room-voice-btn room-voice-btn-secondary"
          type="button"
          onClick={onRefreshDevices}
          disabled={isSwitchingDevice}
        >
          Refresh Devices
        </button>
      </div>

      {joined && !canPublishAudio && (
        <div className="room-voice-error">
          {publishRestrictionReason || 'Your microphone is currently restricted.'}
        </div>
      )}

      {joined && isVoiceLocked && !isRoomOwner && (
        <div className="room-voice-banner">
          <div>Room voice is owner-only right now.</div>
        </div>
      )}

      <div className="room-voice-settings">
        <label className="room-voice-field">
          <span className="room-voice-field-label">Microphone</span>
          <select
            className="room-voice-select"
            value={selectedAudioInputId}
            onChange={(event) => onSelectAudioInput(event.target.value)}
            disabled={isSwitchingDevice || audioInputDevices.length === 0 || (joined && !canPublishAudio)}
          >
            {audioInputDevices.length === 0 ? (
              <option value="">No microphones found</option>
            ) : (
              audioInputDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))
            )}
          </select>
        </label>

        {canSelectAudioOutput && (
          <label className="room-voice-field">
            <span className="room-voice-field-label">Speaker</span>
            <select
              className="room-voice-select"
              value={selectedAudioOutputId}
              onChange={(event) => onSelectAudioOutput(event.target.value)}
              disabled={isSwitchingDevice || audioOutputDevices.length === 0}
            >
              {audioOutputDevices.length === 0 ? (
                <option value="">No speakers found</option>
              ) : (
                audioOutputDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))
              )}
            </select>
          </label>
        )}

        <label className={`room-voice-toggle${joined ? ' is-disabled' : ''}`}>
          <input
            type="checkbox"
            checked={noiseSuppressionEnabled}
            onChange={(event) => onToggleNoiseSuppression(event.target.checked)}
            disabled={joined}
          />
          <span>
            Browser noise suppression
            <span className="room-voice-toggle-note">
              {joined ? ' Changes apply next time you join.' : ' Applied when your mic is created.'}
            </span>
          </span>
        </label>
      </div>

      {error && <div className="room-voice-error">{error}</div>}
      {needsAudioResume && (
        <button
          className="room-voice-btn room-voice-btn-secondary"
          onClick={onStartAudio}
        >
          Resume Audio
        </button>
      )}

      <div className="room-voice-roster">
        {participants.length === 0 ? (
          <div className="room-voice-empty">Nobody is in voice yet.</div>
        ) : (
          participants.map((participant) => (
            <div
              className={`room-voice-user${participant.isSpeaking ? ' is-speaking' : ''}${participant.recentlySpoke ? ' is-recent' : ''}`}
              key={participant.userId}
            >
              <div className="room-voice-avatar">{initials(participant.displayName)}</div>
              <div className="room-voice-user-meta">
                <div className="room-voice-user-name">
                  {participant.displayName}
                  {participant.isSelf ? ' (You)' : ''}
                </div>
                <div className="room-voice-user-state">
                  {getParticipantStateLabel(participant)}
                </div>
              </div>
              <div className="room-voice-user-indicators" aria-hidden="true">
                <span className={`room-voice-user-dot${participant.isMuted ? ' is-muted' : ''}`} />
                <span className={`room-voice-speaker-bars${participant.isSpeaking ? ' is-speaking' : ''}${participant.recentlySpoke ? ' is-recent' : ''}`}>
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
